import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { emailValidation } from '@adornis/users/db/validators.js';
import { validate } from '@adornis/validation/decorators.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';

@Entity()
export class LogistikClearanceEntry extends AdornisEntity {
  static override _class = 'HubClearanceEntry'; // TODO needs migration before we can change this

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;

  @validate(nonOptional())
  @Field(() => Float)
  price!: number;

  @validate(nonOptional())
  @Field(() => String)
  email!: string;
}

@Entity()
export class LogistikSettings extends MongoEntity {
  static override _class = 'HubClearanceSettings'; // TODO needs migration before we can change this
  static override _collectionName = 'hub-clearance-settings'; // TODO needs migration before we can change this

  @validate(nonOptional())
  @Field(() => [LogistikClearanceEntry], { default: v => v ?? [] })
  clearanceEntries!: LogistikClearanceEntry[];

  @validate(nonOptional())
  @Field(() => [String], {
    default: v => v ?? [],
    includeInParameter: true,
    resolve: function (this: LogistikSettings) {
      return async () => {
        if (!this.noMaxOrderAmountEmails) return [];
        return this.noMaxOrderAmountEmails;
      };
    },
  })
  noMaxOrderAmountEmails!: string[];

  @validate(nonOptional())
  @Field(() => [String], {
    default: v => v ?? [],
    includeInParameter: true,
    resolve: function (this: LogistikSettings) {
      return async () => {
        if (!this.frequentOrdersNotificationEmails) return [];
        return this.frequentOrdersNotificationEmails;
      };
    },
  })
  frequentOrdersNotificationEmails: Maybe<string[]>;

  @validate(nonOptional())
  @Field(() => [String], { default: v => v ?? [] })
  selfPrintNotificationEmails: Maybe<string[]>;

  @validate(and(emailValidation, nonOptional()))
  @Field(() => String)
  maxOrderAmountSupportEmail: Maybe<string>;
}

export namespace LogistikSettings {
  export const get = registerQuery({
    type: () => LogistikSettings,
    operationName: 'getLogistikSettings',
    options: {},
    params: [],
    resolve: () => async (gqlFields: BaseQLSelectionSet<LogistikSettings>) => {
      const col = await getRawCollection<LogistikSettings>(LogistikSettings._collectionName);
      const existingSetting = await col.findOne({});
      if (existingSetting) return existingSetting as LogistikSettings;

      const newSetting = new LogistikSettings({});
      await newSetting.create();
      return newSetting;
    },
  });
}
